import React from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Copy,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  CheckCircle2,
  XCircle,
  Zap,
  Building2,
  RefreshCw,
} from "lucide-react";
import DrillDownBreadcrumb from "./DrillDownBreadcrumb";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const getStatusConfig = (t) => ({
  pending: {
    icon: Clock,
    color: "bg-amber-100 text-amber-700",
    label: t?.("pending") ?? "Pending",
  },

  success: {
    icon: CheckCircle2,
    color: "bg-emerald-100 text-emerald-700",
    label: t?.("success") ?? "Success",
  },

  declined: {
    icon: XCircle,
    color: "bg-red-100 text-red-700",
    label: t?.("declined") ?? "Declined",
  },
});

const formatAmountWithCurrency = (amount, currency) => {
  const num = Number(amount || 0);
  const code = String(currency || "PKR").toUpperCase();
  const symbols = { PKR: "Rs", IDR: "Rp" };
  return `${symbols[code] ?? code} ${num.toLocaleString()}`;
};

export default function TransactionDetailPanel({
  transaction,
  open,
  onClose,
  engine: engineOverride,
  reseller: resellerOverride,
  merchant: merchantOverride,
}) {
  const [copiedProvider, setCopiedProvider] = React.useState(false);
  const [copiedSystem, setCopiedSystem] = React.useState(false);

  const { t } = useLanguage();

  const NA = t?.("nA") ?? "N/A";
  const statusConfig = getStatusConfig(t);

  const formatDate = (value, pattern = "MMM d, yyyy HH:mm:ss") => {
    if (!value) return NA;
    const d = new Date(value);
    return isNaN(d.getTime()) ? NA : format(d, pattern);
  };

  const txTypeLabel = (type) => {
    const up = (type || "").toUpperCase();
    if (up === "DEPOSIT") return t?.("deposit") ?? type;
    if (up === "WITHDRAWAL") return t?.("withdrawal") ?? type;
    return type;
  };

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text || "");
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  if (!transaction) return null;

  const statusKey = (transaction.status || "").toLowerCase();
  const status = statusConfig[statusKey] ?? statusConfig.pending;
  const StatusIcon = status.icon;

  const engine = engineOverride ?? transaction.engine;
  const reseller = resellerOverride ?? transaction.reseller;
  const merchant = merchantOverride ?? transaction.merchant;

  const isFailed = ["failed", "declined"].includes(transaction.status);

  const toDisplayText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const callbackMessageText = toDisplayText(
    transaction.callbackResponse ??
      transaction.callback_response ??
      transaction.apiResponse,
  );

  const statusTimeline = [
    {
      key: "created",
      label: t?.("createdLabel") ?? t?.("created") ?? "Created",
      time: transaction.createdAt,
      done: true,
    },
    {
      key: "processing",
      label: t?.("processing") ?? "Processing",
      time: transaction.processedAt,
      done: ["processing", "success", "failed", "recived", "declined"].includes(
        transaction.status,
      ),
    },
    {
      key: isFailed ? "failed" : "success",
      label: isFailed
        ? (t?.("declined") ?? "Declined")
        : (t?.("completed") ?? "Completed"),
      time: transaction.completedAt ?? transaction.updatedAt,
      done:
        ["success", "declined", "jbrecived"].includes(transaction.status) ||
        transaction.callbackStatus === "RECEIVED",
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${transaction.type === "deposit" ? "bg-emerald-100" : "bg-blue-100"}`}
            >
              {transaction.type === "deposit" ? (
                <ArrowDownToLine className="h-5 w-5 text-emerald-600" />
              ) : (
                <ArrowUpFromLine className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <p className="text-lg font-bold">
                {txTypeLabel(transaction.type) ??
                  t?.("transaction") ??
                  "Transaction"}
              </p>
              <Badge className={`${status.color} gap-1 border-0`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </Badge>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Hierarchy */}
          <DrillDownBreadcrumb
            engine={engine}
            reseller={reseller}
            merchant={merchant}
            transaction={transaction}
          />

          {/* Amounts */}
          <div className="grid grid-cols-3 gap-2 py-4 bg-slate-50 rounded-xl px-4">
            {[
              {
                label: t?.("amount") ?? "Amount",
                value: transaction.amount,
                cls: "text-slate-900",
              },
              {
                label: t?.("fee") ?? "Fee",
                value: transaction.feeAmount,
                cls: "text-red-500",
              },
              {
                label: t?.("netAmount") ?? "Net",
                value: transaction.netAmount,
                cls: "text-emerald-600",
              },
            ].map(({ label, value, cls }, i) => (
              <div key={i} className="text-center">
                <p className="text-xs text-slate-500 mb-1">{label}</p>
                <p
                  className={`text-lg font-bold ${cls} ${i < 2 ? "border-r border-slate-200" : ""}`}
                >
                  {formatAmountWithCurrency(value, transaction.currency)}
                </p>
              </div>
            ))}
          </div>

          {/* Transaction IDs */}
          <div className="space-y-4">
            {[
              {
                label: t?.("providerTransactionId") ?? "Provider TX ID",
                value: transaction.providerTransactionId,
                copied: copiedProvider,
                setter: setCopiedProvider,
                mono: "text-sm",
              },
              {
                label: t?.("systemId") ?? "System ID",
                value: transaction.id,
                copied: copiedSystem,
                setter: setCopiedSystem,
                mono: "text-xs",
              },
            ].map(({ label, value, copied, setter, mono }) => (
              <div key={label}>
                <p className="text-sm text-slate-500 mb-2">{label}</p>
                <div className="flex items-center gap-2">
                  <code
                    className={`flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono ${mono} truncate`}
                  >
                    {value}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(value, setter)}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            {merchant?.id && (
              <Link
                to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}
                onClick={onClose}
              >
                <Button
                  variant="outline"
                  className="w-full gap-2 h-auto py-3 justify-start"
                >
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <div className="text-left">
                    <p className="text-[10px] text-slate-400 leading-none">
                      {t?.("merchant") ?? "Merchant"}
                    </p>
                    <p className="text-xs font-medium truncate max-w-[120px]">
                      {merchant.name}
                    </p>
                  </div>
                </Button>
              </Link>
            )}
            {transaction.provider?.id && (
              <Link
                to={
                  createPageUrl("AdminProviderDetail") +
                  `?id=${transaction.provider.id}`
                }
                onClick={onClose}
              >
                <Button
                  variant="outline"
                  className="w-full gap-2 h-auto py-3 justify-start"
                >
                  <Zap className="h-4 w-4 text-amber-500" />
                  <div className="text-left">
                    <p className="text-[10px] text-slate-400 leading-none">
                      {t?.("provider") ?? "Provider"}
                    </p>
                    <p className="text-xs font-medium truncate max-w-[120px]">
                      {transaction.provider.name}
                    </p>
                  </div>
                </Button>
              </Link>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            {[
              {
                label: t?.("paymentMethod") ?? "Payment Method",
                value: transaction.paymentMethod,
              },
              {
                label: t?.("country") ?? "Country",
                value: transaction.country,
              },
              {
                label: t?.("processorBankcode") ?? "Bank Code",
                value: transaction.processorBankcode,
                mono: true,
              },
              {
                label: t?.("payoutType") ?? "Payout Type",
                value: transaction.payoutType?.replace("_", " "),
                capitalize: true,
              },
              {
                label: t?.("platform") ?? "Platform",
                value: transaction.platformName,
              },
              {
                label: t?.("ipAddress") ?? "IP Address",
                value: transaction.ipAddress,
                mono: true,
              },
            ].map(({ label, value, mono, capitalize }) => (
              <div key={label}>
                <p className="text-xs text-slate-500 mb-0.5">{label}</p>
                <p
                  className={`text-sm font-medium ${mono ? "font-mono" : ""} ${capitalize ? "capitalize" : ""}`}
                >
                  {value ?? NA}
                </p>
              </div>
            ))}
          </div>

          {/* Customer Info */}
          {(transaction.customerName ||
            transaction.customerEmail ||
            transaction.customerPhone) && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">
                {t?.("customerInfo") ?? "Customer Info"}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {[
                  {
                    label: t?.("name") ?? "Name",
                    value: transaction.customerName,
                  },
                  {
                    label: t?.("email") ?? "Email",
                    value: transaction.customerEmail,
                  },
                  {
                    label: t?.("phone") ?? "Phone",
                    value: transaction.customerPhone,
                  },
                ]
                  .filter(({ value }) => !!value)
                  .map(({ label, value }) => (
                    <div
                      key={label}
                      className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-200"
                    >
                      <span className="text-xs text-slate-500">{label}</span>
                      <span className="text-sm font-medium">{value}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Callback Status */}
          <div className="bg-slate-900 rounded-xl p-4 text-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <RefreshCw className="h-3 w-3" />
              {t?.("callbackStatus") ?? "Callback Status"}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-slate-500 mb-0.5">
                  {t?.("status") ?? "Status"}
                </p>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full ${transaction.callbackStatus === "SUCCESS" ? "bg-emerald-500" : "bg-red-500"}`}
                  />
                  <span className="text-xs font-mono">
                    {transaction.callbackStatus ?? "PENDING"}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-0.5">
                  {t?.("attempts") ?? "Attempts"}
                </p>
                <span className="text-xs font-mono">
                  {transaction.callbackAttempts} / 5
                </span>
              </div>
            </div>
            {(transaction.callbackResponse || transaction.apiResponse) && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <p className="text-[10px] text-slate-500 mb-2">
                  {t?.("latestResponse") ?? "Latest Response"}
                </p>
                <pre className="text-[10px] font-mono bg-black/30 p-2 rounded h-32 overflow-y-auto text-emerald-400">
                  {JSON.stringify(
                    transaction.callbackResponse ?? transaction.apiResponse,
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>

          {/* Status Timeline */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
              {t?.("statusTimeline") ?? "Status Timeline"}
            </p>
            <div className="space-y-4">
              {statusTimeline.map((step, i) => {
                const isLast = i === statusTimeline.length - 1;
                const isFailedStep = step.key === "failed";
                return (
                  <div key={step.key} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done ? (isFailedStep ? "bg-red-100" : "bg-emerald-100") : "bg-slate-100"}`}
                      >
                        {step.done ? (
                          isFailedStep ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          )
                        ) : (
                          <Clock className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                      {!isLast && (
                        <div
                          className={`w-0.5 h-8 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`}
                        />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <p
                        className={`text-sm font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}
                      >
                        {step.label}
                      </p>
                      {step.time && step.done && (
                        <p className="text-[11px] text-slate-500">
                          {formatDate(step.time)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Metadata */}
          {transaction.metadata &&
            Object.keys(transaction.metadata).length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {t?.("metadata") ?? "Metadata"}
                </p>
                <div className="grid grid-cols-1 gap-1">
                  {Object.entries(transaction.metadata).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-slate-400 capitalize">
                        {key}:
                      </span>
                      <span className="text-xs font-medium text-slate-700">
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Admin Note */}
          {transaction.adminNote && (
            <div className="bg-white border-2 border-dashed border-slate-100 rounded-xl p-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                {t?.("adminInternalNote") ?? "Internal Admin Note"}
              </p>
              <p className="text-sm text-slate-700 font-medium italic">
                "{transaction.adminNote}"
              </p>
            </div>
          )}

          {/* Callback Response */}
          {callbackMessageText && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <p className="text-sm font-medium text-slate-700">{t ? (t("callbackResponse") || "Callback Response") : "Callback Response"}</p>
              <p className="text-sm text-slate-700 mt-1 break-words">{callbackMessageText}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex items-center justify-between text-[10px] text-slate-400 pt-4 border-t">
            <p>
              {t?.("systemCreatedAt") ?? "Sys Created"}:{" "}
              {formatDate(transaction.createdAt)}
            </p>
            <p>
              {t?.("lastModified") ?? "Last Modified"}:{" "}
              {formatDate(transaction.updatedAt)}
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
